import { useGetAgentToolsQuery } from '@saaga/shared/store/agents-api.store'
import { type ReactNode, useMemo } from 'react'
import { Outlet } from 'react-router-dom'

import { mockNotificationList } from '@/components/notifications/notification-mock-data.util'
import { OnboardingWizard } from '@/components/onboarding/onboarding-wizard.organism'
import { useOnboardingWizard } from '@/components/onboarding/use-onboarding-wizard.hook'
import { SidebarInset, SidebarProvider } from '@/components/ui/sidebar'
import { useAnalyticsIdentify } from '@/hooks/use-analytics-identify.hook'
import { useOrganizationEarlyAccess } from '@/hooks/use-organization-early-access.hook'
import { usePageTracking } from '@/hooks/use-page-tracking.hook'

import { useAppLayout } from './app-layout.hook'
import { AppLayoutDialogs } from './app-layout-dialogs.molecule'
import { AppLayoutHeader } from './app-layout-header.molecule'
import { AppSidebar } from './app-sidebar.organism'

interface AppLayoutProps {
  children?: ReactNode
}

type AppLayoutState = ReturnType<typeof useAppLayout>

const AppLayoutSidebarInset = ({
  state,
  handlers,
  children,
}: AppLayoutState & { children?: ReactNode }): ReactNode => (
  <SidebarInset>
    <AppLayoutHeader
      searchResultGroupList={state.searchResultGroupList}
      isSearching={state.isSearching}
      notificationList={mockNotificationList}
      onGlobalSearch={handlers.handleGlobalSearch}
      onResultSelect={handlers.handleResultSelect}
      onAgenciesClick={handlers.handleAgenciesClick}
      onConnectsClick={handlers.handleConnectsClick}
      onOrgSettings={handlers.handleOrgSettings}
      onNotificationClick={handlers.handleNotificationClick}
      onMarkAllRead={handlers.handleMarkAllRead}
    />
    <main className="layout-main-wrapper h-[calc(100svh-theme(spacing.14))] lg:h-[calc(100svh-60px)] overflow-hidden">
      {children}
    </main>
  </SidebarInset>
)

export const AppLayout = ({ children }: AppLayoutProps): ReactNode => {
  useAnalyticsIdentify()
  usePageTracking()
  const { state, handlers } = useAppLayout()
  const { isEarlyAccess } = useOrganizationEarlyAccess()
  const onboarding = useOnboardingWizard()
  const { data: toolsData } = useGetAgentToolsQuery({ pageSize: 200 }, { skip: !onboarding.isOpen })
  const toolNameList = useMemo(() => toolsData?.toolList.map((t) => t.name) ?? [], [toolsData])

  return (
    <>
      <OnboardingWizard
        isOpen={onboarding.isOpen}
        isLoading={onboarding.isLoading}
        onClose={onboarding.handleClose}
        onSkip={onboarding.handleSkip}
        onSubmit={onboarding.handleSubmit}
        toolNameList={toolNameList}
        defaultWebsiteUrl={onboarding.defaultWebsiteUrl}
      />
      <SidebarProvider>
        <AppSidebar
          user={state.userData}
          activeOrganization={state.activeOrgData}
          organizations={state.organizations}
          navGroups={state.navGroups}
          subscriptionInfo={state.subscriptionInfo}
          isEarlyAccess={isEarlyAccess}
          onSignOut={handlers.handleSignOut}
          onOrganizationSelect={handlers.handleOrganizationSelect}
          onCreateOrganization={handlers.handleCreateOrganization}
          onNewChat={handlers.handleCreateChat}
        />
        <AppLayoutSidebarInset state={state} handlers={handlers}>
          {children ?? <Outlet />}
        </AppLayoutSidebarInset>
      </SidebarProvider>
      <AppLayoutDialogs
        createOrgDialog={state.createOrgDialog}
        chatDeleteDialog={state.chatDeleteDialog}
        onCreateOrgDialogChange={handlers.handleCreateOrgDialogChange}
        onCreateOrgSubmit={handlers.handleCreateOrgSubmit}
      />
    </>
  )
}
